import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAirbnbUrl, isRealAirbnbUrl } from "@/hooks/useAirbnbUrl";

interface Accommodation {
  id: number;
  name: string;
  area: string;
  tag: string;
  hashtags: string[];
  rating: number;
  reviewCount: number;
  price: string;
  maxGuests: number;
  beds: number;
  features: string[];
  airbnbUrl: string;
  image: string;
  hostName: string;
  needsRealPhoto?: boolean;
}

interface Props {
  item: Accommodation;
  index: number;
}

export default function AccommodationCard({ item, index }: Props) {
  const [imgError, setImgError] = useState(false);
  const [showPhotoHint, setShowPhotoHint] = useState(false);
  const navigate = useNavigate();
  const airbnbUrl = useAirbnbUrl(item.id, item.airbnbUrl);
  const hasRealUrl = isRealAirbnbUrl(airbnbUrl);

  return (
    <div
      className="bg-white rounded-2xl overflow-hidden group hover:scale-[1.02] transition-all duration-300 border border-stone-100 flex flex-col"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {/* Image */}
      <div
        className="relative w-full h-52 overflow-hidden cursor-pointer"
        onMouseEnter={() => setShowPhotoHint(true)}
        onMouseLeave={() => setShowPhotoHint(false)}
      >
        <img
          src={
            imgError
              ? "https://public.readdy.ai/ai/img_res/c2b4a7a5e39b0829cf7ca6c5b9ffbdaf.jpg"
              : item.image
          }
          alt={item.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          onError={() => setImgError(true)}
        />

        {/* 실사진 교체 오버레이 힌트 */}
        {item.needsRealPhoto && showPhotoHint && (
          <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center transition-all duration-200">
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full mb-2">
              <i className="ri-camera-line text-white text-xl"></i>
            </div>
            <p className="text-white text-xs font-semibold text-center px-4 leading-snug">
              실제 숙소 사진으로<br />교체 예정
            </p>
          </div>
        )}

        {/* 실사진 교체 필요 배지 */}
        {item.needsRealPhoto && (
          <div className="absolute bottom-2 right-2">
            <span className="bg-black/60 backdrop-blur-sm text-white/80 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
              <i className="ri-camera-line text-xs"></i>
              사진 교체 예정
            </span>
          </div>
        )}

        {/* Tag */}
        <div className="absolute top-3 left-3">
          <span className="bg-amber-500/90 backdrop-blur-sm text-white text-xs font-medium px-3 py-1 rounded-full">
            {item.tag}
          </span>
        </div>
        {/* Area Badge */}
        <div className="absolute top-3 right-3">
          <span className="bg-white/90 backdrop-blur-sm text-stone-700 text-xs font-semibold px-3 py-1 rounded-full">
            {item.area}
          </span>
        </div>
      </div>

      {/* Hashtags */}
      <div className="flex flex-wrap gap-1 px-4 pt-3">
        {item.hashtags.map((tag) => (
          <span
            key={tag}
            className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full font-medium"
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Content */}
      <div className="p-4 pt-2 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="text-sm font-bold text-stone-800 leading-snug line-clamp-2">{item.name}</h3>
          <div className="flex items-center gap-1 shrink-0 text-amber-500">
            <i className="ri-star-fill text-xs"></i>
            <span className="text-xs font-semibold text-stone-700">{item.rating}</span>
          </div>
        </div>

        <p className="text-xs text-stone-400 mb-3">
          {item.reviewCount}개 후기 · 최대 {item.maxGuests}인 · 침실 {item.beds}개
        </p>

        {/* Features */}
        <div className="flex flex-wrap gap-1 mb-4 flex-1 content-start">
          {item.features.map((f) => (
            <span key={f} className="text-xs bg-stone-50 text-stone-600 px-2.5 py-1 rounded-full border border-stone-100">
              {f}
            </span>
          ))}
        </div>

        {/* Bottom */}
        <div className="flex items-center justify-between pt-3 border-t border-stone-100 mt-auto">
          <div>
            <span className="text-base font-bold text-stone-800">{item.price}</span>
            <span className="text-xs text-stone-400"> /박</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`/accommodation/${item.id}`)}
              className="inline-flex items-center gap-1.5 bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs font-semibold px-3 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              상세보기
              <i className="ri-arrow-right-line text-xs"></i>
            </button>
            {hasRealUrl ? (
              <a
                href={airbnbUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-3 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                예약하기
                <i className="ri-external-link-line text-xs"></i>
              </a>
            ) : (
              <span
                className="inline-flex items-center gap-1.5 bg-stone-100 text-stone-400 text-xs font-medium px-3 py-2 rounded-full whitespace-nowrap cursor-default"
                title="어드민에서 에어비앤비 링크를 설정해주세요"
              >
                <i className="ri-link-unlink text-xs"></i>
                링크 준비중
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
